import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ModifyDashboardView } from '@futrli/views/views/reports/ModifyDashboardView/ModifyDashboardView';
import { useApiView } from '@futrli/views/hooks/useApiView';

interface RemoveFromPerformanceAreaDialogProps {
  dashboardId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const RemoveFromPerformanceAreaDialog = ({
  dashboardId,
  isOpen,
  onClose
}: RemoveFromPerformanceAreaDialogProps) => {
  const intl = useIntl();
  const { workspaceId = '', organisationId = '' } = useParams();

  const view = useApiView(
    useMemo(() => new ModifyDashboardView(workspaceId, organisationId, dashboardId), [
      workspaceId,
      organisationId,
      dashboardId
    ])
  );

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isRemovingFromPerformanceArea,
    error: view.state.errorRemovingFromPerformanceArea,
    onAction: () => view.removeFromPerformanceArea(),
    onClose
  });

  const title = intl.formatMessage({
    id: 'predict.dashboards.remove_from_performance_area',
    defaultMessage: 'Remove from performance area'
  });

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleAction}
      screen={screen}
      title={title}
      subtitle={view.state.dashboard?.name}
      confirmTitle={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
      confirmDescription={intl.formatMessage({
        id: 'predict.dashboards.remove_from_performance_area_description',
        defaultMessage: 'All users will lose access to this dashboard in the performance dashboards tab'
      })}
      loadingMessage={intl.formatMessage({
        id: 'predict.dashboards.removing_from_performance_area',
        defaultMessage: 'Removing from performance area...'
      })}
      successMessage={intl.formatMessage({
        id: 'predict.dashboards.dashboard_removed_from_performance_area',
        defaultMessage: 'Dashboard removed from performance area'
      })}
      errorMessage={view.state.errorRemovingFromPerformanceArea?.message}
      dataTestId="remove-from-performance-area-dialog"
    />
  );
};
